// src/components/CatalogueDownloadSection.js

import React, { useState, useEffect, useRef } from 'react';
import './CatalogueDownloadSection.css';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ReCAPTCHA from 'react-google-recaptcha';

// --- IMPORTANT: Update your image paths here ---
import catalogueCoverImage from '../Images/Light3.jpg'; 
import SmartlightLogoBlue from '../Images/SmartlightLogoBlue.png'; // Make sure you have a dark logo

// --- VALIDATION SCHEMA FOR THE FORM ---
const catalogueSchema = yup.object().shape({
  from_name: yup.string().required('Your name is required.'),
  from_email: yup.string().email('Please enter a valid email.').required('Your email is required.'),
  from_phone: yup.string().required('A valid phone number is required.'),
  message: yup.string().required('Please tell us about your project or inquiry.'),
  recaptcha: yup.string().required('Please verify you are not a robot.'),
});


// ==========================================================
//      POP-UP FORM SUB-COMPONENT
// ==========================================================
const DownloadFormPopup = ({ onClose, onSuccessfulSubmit }) => {
  // --- IMPORTANT: REPLACE THESE PLACEHOLDERS ---
  const SCRIPT_URL = "https://script.google.com/macros/s/AKfycbzJYFBgsdhes41k5I8mjvTBDCnqIATz_P_EGlAdx-8wRyFY5G3GSZrguR9ZcUNf86Mp/exec";
  const RECAPTCHA_SITE_KEY = "6LdG4tcrAAAAACrpa9DG-_S4Jv4SFph_gFP4N8rj";

  const [status, setStatus] = useState({ type: '', message: '' });
  const { register, handleSubmit, reset, control, formState: { errors, isSubmitting } } = useForm({
    resolver: yupResolver(catalogueSchema),
  });

  const onSubmit = async (data) => {
    setStatus({ type: 'info', message: 'Submitting...' });
    
    const payload = {
      name: data.from_name,
      email: data.from_email,
      phone: data.from_phone,
      message: data.message,
      recaptchaToken: data.recaptcha,
    };

    try {
      const response = await fetch(SCRIPT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=utf-8' },
        body: JSON.stringify(payload),
      });
      const result = await response.json();
      
      if (result.result === 'success') {
        setStatus({ type: 'success', message: 'Success! Your download will begin shortly.' });
        reset();
        onSuccessfulSubmit(); // Trigger the download in the parent
        setTimeout(() => onClose(), 2000); // Close popup after 2 seconds
      } else {
        throw new Error(result.message || 'Verification failed.');
      }
    } catch (error) {
      setStatus({ type: 'error', message: `Submission failed: ${error.message}` });
    }
  };
  
  // Effect for closing with 'Escape' key
  useEffect(() => {
    const handleKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="popup-close-btn" onClick={onClose} aria-label="Close">&times;</button>
        <div className="popup-header">
          <h3>Download Our Catalogue</h3>
          <p>Please provide your details to start the download.</p>
        </div>
        <form className="popup-form" onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="form-group"><input {...register('from_name')} placeholder="Full Name" />{errors.from_name && <span className="error-message">{errors.from_name.message}</span>}</div>
          <div className="form-group"><input {...register('from_email')} placeholder="Email Address" />{errors.from_email && <span className="error-message">{errors.from_email.message}</span>}</div>
          <div className="form-group"><input {...register('from_phone')} placeholder="Phone Number" />{errors.from_phone && <span className="error-message">{errors.from_phone.message}</span>}</div>
          <div className="form-group"><textarea {...register('message')} rows="4" placeholder="Message / Project Details"></textarea>{errors.message && <span className="error-message">{errors.message.message}</span>}</div>
          <div className="form-group">
            <Controller name="recaptcha" control={control} render={({ field }) => (<ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} onChange={(token) => field.onChange(token)} />)} />
            {errors.recaptcha && <span className="error-message">{errors.recaptcha.message}</span>}
          </div>
          <button type="submit" className="popup-submit-btn" disabled={isSubmitting}>{isSubmitting ? 'Processing...' : 'Submit & Download'}</button>
          {/* {status.message && <p className={`popup-status-message status-${status.type}`}>{status.message}</p>} */}
        </form>
      </div>
    </div>
  );
};


// ==========================================================
//      MAIN CATALOGUE SECTION COMPONENT
// ==========================================================
const CatalogueDownloadSection = () => {
  const [isInView, setIsInView] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const sectionRef = useRef(null);

  const handleDownloadTrigger = () => {
    const link = document.createElement('a');
    link.href = '/Documents/SMARTLIGHT_CATLOGUE.pdf'; // Path from your project's `public` folder
    link.setAttribute('download', 'SMARTLIGHT_CATLOGUE.pdf');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleOpenPopup = () => setIsPopupVisible(true);
  const handleClosePopup = () => setIsPopupVisible(false);
  
  // Effect to lock/unlock background scroll when popup is active
  useEffect(() => {
    if (isPopupVisible) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [isPopupVisible]);

  // Effect to trigger animations when section scrolls into view
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsInView(entry.isIntersecting);
    }, { threshold: 0.3 }); 

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }
    return () => { 
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []); 

  return (
    <>
      <section ref={sectionRef} className={`catalogue-section-split ${isInView ? 'is-in-view' : ''}`}>
        <div className="text-panel">
          <div className="text-content">
            <h1 className="split-headline">Explore Our Curated Collection</h1>
            <p className="split-description">
              Discover the perfect blend of form and function. Download our latest catalogue to see our full range of innovative and inspiring lighting solutions.
            </p>
            {/* <button className="cta-button-split" onClick={handleOpenPopup}>
              Download Now
            </button> */}
          </div>
        </div>
        
        {/* === UPDATED JSX FOR THE NEW IMAGE PANEL DESIGN === */}
        <div className="image-panel">
          <div className="logo-subpanel">
            <img src={SmartlightLogoBlue} alt="Smartlight Logo" className="rotated-logo" />
          </div>
          <div className="image-subpanel">
            <div className="image-bg" style={{ backgroundImage: `url(${catalogueCoverImage})` }}></div>
          </div>
        </div>

      </section>
      
      {isPopupVisible && <DownloadFormPopup onClose={handleClosePopup} onSuccessfulSubmit={handleDownloadTrigger} />}
    </>
  );
};

export default CatalogueDownloadSection;